import { Grid, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { addBusinessDays, format, isSameDay } from "date-fns";
import { useEffect, useState } from "react";
import { DateWithSlots } from "../../schedule-one-on-one/schedule-one-on-one.constants";
import ToggleSlot from "../../toggle-slot/toggle-slot.component";
import ScheduleCalendar from "../schedule-calendar/schedule-calendar.component";
import { useStyle } from "./available-dates.styles";

interface Props {
  datesWithSlots?: DateWithSlots[];
  selected?: DateWithSlots;
  onChange?: (selected: DateWithSlots) => void;
  onDateChange?: (date: Date) => void;
  loading?: boolean;
  error?: boolean;
}

export default function AvailableDates({
  datesWithSlots,
  selected: propSelected,
  onChange,
  onDateChange,
  loading,
  error,
}: Props) {
  const classes = useStyle();

  const [selected, setSelected] = useState<DateWithSlots>(propSelected);

  useEffect(() => propSelected && setSelected(propSelected), [propSelected]);

  const handleChange = (dateWithSlots: DateWithSlots) => {
    setSelected(dateWithSlots);
    onChange?.(dateWithSlots);
  };

  const handleCalendarChange = (date: Date) => {
    if (!selected || !isSameDay(date, selected.value)) {
      const found = datesWithSlots?.find((d) => isSameDay(d.value, date));
      if (found) {
        handleChange(found);
      } else {
        setSelected(null);
        onDateChange?.(date);
      }
    }
  };

  const handleCalendarStep = (step: number) => {
    if (!datesWithSlots) return;
    if (!selected) {
      handleChange(step < 0 ? datesWithSlots[2] : datesWithSlots[0]);
    } else {
      const currentIndex = datesWithSlots.findIndex((d) =>
        isSameDay(d.value, selected.value)
      );
      const newIndex = currentIndex + step;
      if (datesWithSlots[newIndex]) {
        handleChange(datesWithSlots[newIndex]);
      } else {
        const newDate = addBusinessDays(selected.value, step);
        setSelected(null);
        onDateChange?.(newDate);
      }
    }
  };

  return (
    <Grid container className={classes.container}>
      <Grid item>
        <Typography className={classes.titleSection}>
          Meet With Your Mentor
        </Typography>
      </Grid>
      <ScheduleCalendar
        date={selected?.value ?? datesWithSlots?.[0].value ?? new Date()}
        onChange={handleCalendarChange}
        onStep={handleCalendarStep}
        disabled={loading}
      />
      <Grid item container justifyContent="space-between">
        {loading ? (
          Array(3)
            .fill(0)
            .map((_, index) => (
              <Grid key={index} item className={classes.gridSlot}>
                <Skeleton
                  width={84}
                  height={84}
                  variant="rect"
                  className={classes.skeleton}
                />
              </Grid>
            ))
        ) : error ? (
          <Typography className={classes.error}>
            It wasn't possible to load available dates for the chosen day.
            Please try again or use the calendar to choose another day.
          </Typography>
        ) : (
          datesWithSlots?.map((item) => (
            <Grid
              key={item.value.toISOString()}
              item
              className={classes.gridSlot}
            >
              <ToggleSlot
                isSelected={!!selected && isSameDay(item.value, selected.value)}
                title={format(item.value, "EEE")}
                content={format(item.value, "dd MMM")}
                footer={`${item.slots.length} slots`}
                onClick={() => handleChange(item)}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Grid>
  );
}
